import { Modal, Typography, Divider, Tag, Space } from 'antd';

const { Title, Paragraph, Text } = Typography;

/**
 * 数据集解析器使用说明（P1）
 * 入口：数据集调试台「② 解析器（JSON）」旁的 📖 图标
 */
export default function ParserHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      title="解析器 · 使用说明与示例"
      data-testid="parser-help-modal"
    >
      <Typography style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
        <Title level={5} style={{ marginTop: 0 }}>一、管线顺序（依次执行）</Title>
        <Paragraph style={{ marginBottom: 8 }}>
          <Text code>path</Text> 取值 → <Text code>mapping</Text> 字段映射 → <Text code>filter</Text> 过滤 →{' '}
          <Text code>transform</Text> JS 转换。任一步失败会显示在「③ 解析结果」的错误区。
        </Paragraph>

        <Title level={5}>二、可用字段与方法</Title>
        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>path</Text>：取值路径，支持 <Text code>a.b.c</Text>、<Text code>a[0].b</Text>、<Text code>a.0.b</Text>；
          留空 = 原始报文整体。
        </Paragraph>
        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>mapping[]</Text>：<Text code>{'{"sourceField":"ts","targetField":"label","type":"number|percent|thousand|date|string|jsonpath","format":"1|YYYY-MM-DD|..."}'}</Text>
          ；<Text code>sourceField</Text> 支持点路径。
        </Paragraph>
        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>filter</Text>：对每行求值，变量 <Text code>item</Text>、<Text code>index</Text>、<Text code>data</Text>；返回 <Text code>true</Text> 保留。
        </Paragraph>
        <Paragraph style={{ marginBottom: 6 }}>
          <Text strong>transform</Text>：对整份数据求值，变量 <Text code>data</Text>；返回任意结构（数组行建议 <Text code>{'{label,value}'}</Text>，折线图直接可用）。
          在浏览器内执行，标准 JS 可用（<Text code>JSON/Math/Date/Number/String/Array/Object/parseFloat/parseInt/isNaN/isFinite</Text>）。
        </Paragraph>

        <Divider style={{ margin: '12px 0' }} />
        <Title level={5}>三、示例（可直接粘贴到解析器）</Title>

        <Paragraph style={{ marginBottom: 4 }}><Text strong>示例 1 · 取值</Text> <Tag>path</Tag></Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          取嵌套字段（如 MQTT 原始报文 <Text code>data.points.temp</Text>）：
          <br /><Text code>{'{"path":"data.points.temp"}'}</Text>
        </Paragraph>

        <Paragraph style={{ marginBottom: 4 }}><Text strong>示例 2 · 字段映射 + 数值格式化</Text> <Tag>path+mapping</Tag></Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          <Text code>{'{"path":"items","mapping":[{"sourceField":"ts","targetField":"label"},{"sourceField":"v","targetField":"value","type":"number","format":"1"}]}'}</Text>
        </Paragraph>

        <Paragraph style={{ marginBottom: 4 }}><Text strong>示例 3 · 过滤</Text> <Tag>filter</Tag></Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          只保留有效且非异常的行：<Text code>{'{"filter":"item.value > 0 && item.level !== \'bad\'"}'}</Text>
        </Paragraph>

        <Paragraph style={{ marginBottom: 4 }}><Text strong>示例 4 · 单位换算（摄氏→华氏，保留 1 位）</Text> <Tag color="blue">transform</Tag></Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          <Text code>{'{"transform":"data.map(function(x){return {label:x.label,value:+(+x.value*1.8+32).toFixed(1)}})"}'}</Text>
        </Paragraph>

        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>示例 5 · 复杂算法：3 点滑动平均 + 阈值分级</Text> <Tag color="purple">transform</Tag>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 10 }}>
          平滑抖动并按均值分级（&gt;5 = high）：
          <br /><Text code>{'{"transform":"data.map(function(x,i,a){var w=a.slice(Math.max(0,i-2),i+1);var avg=w.reduce(function(s,y){return s+Number(y.value)},0)/w.length;return {label:x.label,value:+avg.toFixed(2),level:avg>5?\'high\':\'low\'}})"}'}</Text>
        </Paragraph>

        <Paragraph style={{ marginBottom: 4 }}><Text strong>示例 6 · 全链路组合</Text> <Tag color="green">path+mapping+filter+transform</Tag></Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          先取 <Text code>list</Text> → 映射 → 过滤 0 值 → 转百分比：
          <br /><Text code>{'{"path":"list","mapping":[{"sourceField":"ts","targetField":"label"},{"sourceField":"v","targetField":"value"}],"filter":"item.value > 0","transform":"data.map(function(x){return {label:x.label,value:+(x.value*100).toFixed(1)}})"}'}</Text>
        </Paragraph>

        <Divider style={{ margin: '12px 0' }} />
        <Title level={5}>四、AI 一键生成（推荐）</Title>
        <Paragraph style={{ marginBottom: 6 }}>
          在「可视化（推荐）」模式点「<Text strong>AI 生成</Text>」，填写数据要求、目标字段（可选）和协议；
          AI 会生成规格并自动试跑，采用前请核对字段与结果。
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 4 }}>示例需求（可直接复制）：</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>1. 每行 label/value；输出 label/value</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>2. temp ×0.1 保留 1 位；hum→humidity</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>3. 只保留 value&gt;3 的行；输出 value</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>4. value&gt;5 输出 level=high，否则 low；输出 value/level</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>5. data.items 行；meta.ts→time，meta.value→value</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>6. 最多 2 行；输出 value</Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          更多示例：AI 弹窗右上角「?」可查看并一键填入；跨行滑动平均/二进制字节解析请用「代码（高级）」。
        </Paragraph>

        <Divider style={{ margin: '12px 0' }} />
        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          提示：解析器只做「报文 → 业务行数据」，不发起外部请求；保存后所有引用该数据集的组件立即生效。
        </Paragraph>
      </Typography>
    </Modal>
  );
}
